import React, { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { t } from 'src/translations'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  use3pdConfig,
  useFulfillment
} from 'src/public/components/online_ordering/FulfillmentContext'
import { usePayment } from 'src/public/components/online_ordering/PaymentContext'
import { useCart } from 'public/components/online_ordering/CartContext'
import InputField from 'src/shared/components/common/form_input/InputField'
import TipSelector from '../../payment/TipSelector'
import { getUniqueTipOptions } from 'src/shared/js/utils/tipUtils'

export const TipSection = () => {
  const { selectedDiningOptionBehavior } = useFulfillment()
  const { uses3pd } = use3pdConfig()
  const { setTipAmount, setTipPercent, tipEnabled } = usePayment()
  const { cart } = useCart()
  const { setValue, watch } = useFormContext()
  const selectedTipButton = watch('selectedTipButton', '')
  const [customTipAmount, setCustomTipAmount] = React.useState<
    number | undefined
  >(undefined)

  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
  const is3pdDelivery = isDelivery && uses3pd

  // Use precomputed tips from cart with deduplication
  const tipOptions = React.useMemo(() => {
    return getUniqueTipOptions(cart?.preComputedTips || [])
  }, [cart?.preComputedTips])

  const onTipButtonClick = useCallback(
    (buttonId: string, tipValue: number, tipPercent: number) => {
      setValue('selectedTipButton', buttonId)
      setTipAmount(tipValue)
      setTipPercent(tipPercent)
      // Clear custom tip when selecting a precomputed tip
      setCustomTipAmount(undefined)
    },
    [setValue, setTipAmount, setTipPercent]
  )

  const onClickCustomTip = useCallback(() => {
    setValue('selectedTipButton', 'custom-tip')
    const tipValue = watch('custom-tip-amount')
    if (tipValue === undefined || tipValue === null || tipValue === '') {
      setTipAmount(0)
      setTipPercent(undefined)
    } else {
      const tipAmount = Number.parseFloat(tipValue)
      if (isNaN(tipAmount)) {
        setTipAmount(0)
        setTipPercent(undefined)
      } else {
        setTipAmount(Math.max(tipAmount, 0))
        setTipPercent(undefined)
      }
    }
  }, [setValue, watch, setTipAmount, setTipPercent])

  const validateCustomTip = useCallback(
    (value: string) => {
      if (selectedTipButton !== 'custom-tip' || !value || value.trim() === '') {
        return true
      }
      const tipAmount = Number.parseFloat(value)
      if (isNaN(tipAmount)) {
        return t('tip-selector.please-enter-amount')
      }
      if (tipAmount < 0) {
        return t('tip-selector.cannot-have-negative-tip')
      }
      return true
    },
    [selectedTipButton]
  )

  useEffect(() => {
    if (!tipEnabled) {
      setTipAmount(0)
      setTipPercent(undefined)
    } else if (is3pdDelivery) {
      const defaultTip = tipOptions.find((tip) => tip.percent === 20)
      if (defaultTip) {
        setValue('selectedTipButton', `tip-button-${defaultTip.percent}`)
        setTipAmount(defaultTip.value)
        setTipPercent(defaultTip.percent)
      }
    }
  }, [
    tipEnabled,
    setTipAmount,
    setTipPercent,
    setValue,
    is3pdDelivery,
    tipOptions
  ])

  // Only show tip section for delivery orders
  if (!isDelivery) {
    return null
  }

  return (
    <TipSelector
      tipOptions={tipOptions}
      selectedTipButton={selectedTipButton}
      onTipButtonClick={onTipButtonClick}
      onClickCustomTip={onClickCustomTip}
      customTipValue={customTipAmount}
      customTipInputComponent={
        <InputField
          id='custom-tip-amount'
          label={t('tip-selector.tip-amount')}
          type='number'
          step='any'
          min='0'
          value={customTipAmount}
          validate={validateCustomTip}
          onChange={(e) => {
            const value = e.target.value
            if (!value || value.trim() === '') {
              setCustomTipAmount(undefined)
              setTipAmount(0)
              return
            }
            const amount = Number.parseFloat(value)
            if (!isNaN(amount)) {
              const roundedAmount = Math.round(amount * 100) / 100
              setCustomTipAmount(roundedAmount)
              setTipAmount(roundedAmount)
            }
          }}
          preventFormSubmit
        />
      }
    />
  )
}

export default TipSection
